import * as React from "react";
import { cn } from "@/lib/utils";
import { MenuButton } from "./ui/menu-button";
import { MobileMenu, MenuItem } from "./MobileMenu";

// ANYW3AR logo (SVG from Figma)
const ANYW3AR_LOGO = "/images/logo-typo-white.svg";

export interface MobileHeaderProps {
  /**
   * Optional custom menu items
   */
  menuItems?: MenuItem[];
  /**
   * Optional callback when a menu item is selected
   */
  onNavigate?: (itemId: string) => void;
  /**
   * Whether the user is authenticated (for auth-aware menu navigation)
   */
  isAuthenticated?: boolean;
  /**
   * Whether the header should be fixed at the top
   * @default true
   */
  fixed?: boolean;
  /**
   * Additional className for the header element
   */
  className?: string;
  /**
   * Optional logo source (defaults to ANYW3AR logo)
   */
  logoSrc?: string;
  /**
   * Optional logo alt text
   */
  logoAlt?: string;
  /**
   * Optional click handler for logo
   */
  onLogoClick?: () => void;
}

/**
 * MobileHeader - Fixed mobile header with animated menu
 *
 * Features:
 * - Fixed position header bar with ANYW3AR branding
 * - Integrated MenuButton for opening/closing the menu
 * - Full-screen animated menu overlay
 * - Responsive design optimized for mobile devices
 *
 * @example
 * ```tsx
 * <MobileHeader
 *   onNavigate={(itemId) => navigate(`/${itemId}`)}
 *   onLogoClick={() => navigate('/')}
 * />
 * ```
 */
export const MobileHeader = React.forwardRef<HTMLElement, MobileHeaderProps>(
  ({
    menuItems,
    onNavigate,
    isAuthenticated,
    fixed = true,
    className,
    logoSrc = ANYW3AR_LOGO,
    logoAlt = "ANYW3AR",
    onLogoClick
  }, ref) => {

    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    return (
      <>
        {/* Header Bar */}
        <header
          ref={ref}
          className={cn(
            "flex h-[3.75rem] items-center justify-between bg-brand-red px-2 pr-4",
            fixed && "fixed left-0 right-0 top-0 z-40",
            className
          )}
        >
          {/* Left spacer (for visual balance) */}
          <div className="h-[4px] w-[1.875rem]" aria-hidden="true" />

          {/* Logo */}
          <button
            onClick={onLogoClick}
            className={cn(
              "flex flex-1 items-center justify-center",
              onLogoClick && "cursor-pointer hover:opacity-90 transition-opacity"
            )}
            aria-label={`${logoAlt} - Go to home`}
            disabled={!onLogoClick}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={logoAlt}
                className="h-[2.375rem] w-auto"
              />
            ) : (
              <span className="font-montserrat text-2xl font-bold text-white">
                {logoAlt}
              </span>
            )}
          </button>

          {/* Menu Button */}
          <MenuButton
            isOpen={isMenuOpen}
            onToggle={() => setIsMenuOpen(!isMenuOpen)}
            className="relative z-50"
          />
        </header>

        {/* Spacer for fixed header */}
        {fixed && <div className="h-[3.75rem]" aria-hidden="true" />}

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          items={menuItems}
          onNavigate={onNavigate}
          isAuthenticated={isAuthenticated}
        />
      </>
    );
  }
);

MobileHeader.displayName = "MobileHeader";